import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Wallet, AlertTriangle } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { SERVICE_META, fmtRp } from "@/lib/constants";
import { cn } from "@/lib/utils";

export function BudgetSummary({ compact, refreshKey }) {
  const { can } = useAuth();
  const allowed = can("supervisor", "ga_staff");
  const { data } = useFetch("/budgets/status", [refreshKey], allowed);
  if (!allowed || !data) return null;
  const rows = data.budgets || [];
  const tot = data.total || {};
  const canEdit = can("manager", "coo");
  return (
    <section className="op-card p-4" data-testid="budget-summary">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-semibold md:text-lg"><Wallet className="h-5 w-5 text-primary" />Budget GA — periode berjalan</h2>
        {canEdit && <Link to="/master?tab=budgets" className="text-xs text-primary hover:underline" data-testid="budget-manage-link">Kelola budget</Link>}
      </div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">Belum ada budget untuk bulan/tahun ini. {canEdit ? "Tambahkan di Master Data → Budget." : ""}</p> : (<>
        <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="budget-total">
          {[["Total Budget", tot.amount, ""], ["Terpakai", tot.used, "text-amber-500"], ["Dalam Pengajuan", tot.pending, "text-blue-500"], ["Sisa", tot.remaining, tot.remaining < 0 ? "text-red-500" : "text-emerald-500"]].map(([l, v, c]) => <div key={l}><div className="data-label">{l}</div><div className={cn("font-mono text-sm font-bold sm:text-base", c)}>{fmtRp(v)}</div></div>)}
        </div>
        <div className={cn("grid gap-2", compact ? "" : "md:grid-cols-2")}>
          {rows.map((b) => {
            const pct = Math.min(100, b.pct_used || 0); const over = b.remaining < 0;
            return (
              <div key={b.id} className={cn("rounded-md border p-2.5 text-xs", over && "border-red-500/50 bg-red-500/5")} data-testid={`budget-row-${b.code}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><div className="truncate font-semibold">{b.name}</div><div className="truncate text-muted-foreground">{b.period} · {b.service_type ? SERVICE_META[b.service_type]?.title : "Semua layanan"}{b.entity_name ? ` · ${b.entity_name}` : ""}{b.department_name ? ` · ${b.department_name}` : ""}</div></div>
                  <div className={cn("shrink-0 font-mono font-bold", over ? "text-red-500" : "text-emerald-600 dark:text-emerald-400")}>{over && <AlertTriangle className="mr-1 inline h-3 w-3" />}{fmtRp(b.remaining)}</div>
                </div>
                <div className="mt-1.5 flex h-2 overflow-hidden rounded-full bg-muted"><div className="bg-amber-500" style={{ width: `${Math.min(100, (b.used / (b.amount || 1)) * 100)}%` }} /><div className="bg-blue-500/70" style={{ width: `${Math.min(100 - Math.min(100, (b.used / (b.amount || 1)) * 100), (b.pending / (b.amount || 1)) * 100)}%` }} /></div>
                <div className="mt-1 flex justify-between font-mono text-[10px] text-muted-foreground"><span>Terpakai {fmtRp(b.used)} · Pengajuan {fmtRp(b.pending)}</span><span>{pct}% dari {fmtRp(b.amount)}</span></div>
              </div>
            );
          })}
        </div>
      </>)}
    </section>
  );
}

export function BudgetHint({ serviceType, locationId, cost, onOver }) {
  const { data } = useFetch(serviceType ? `/budgets/status?service_type=${serviceType}&location_id=${locationId || ""}&cost=${Number(cost) || 0}` : null, [serviceType, locationId, cost], !!serviceType);
  const over = !!data?.over_budget;
  useEffect(() => { onOver?.(over); }, [over, onOver]);
  if (!data || !data.budgets?.length) return null;
  return (
    <div className="space-y-1" data-testid="budget-hint">
      {data.budgets.map((b) => {
        const over = (Number(cost) || 0) > b.remaining;
        return <p key={b.id} className={cn("text-[11px]", over ? "font-semibold text-red-500" : "text-muted-foreground")} data-testid={`budget-hint-${b.code}`}>{over && <AlertTriangle className="mr-1 inline h-3 w-3" />}Budget {b.name} ({b.period}): sisa {fmtRp(b.remaining)} dari {fmtRp(b.amount)}{over ? " — MELEBIHI BUDGET, approval otomatis naik ke COO" : ""}</p>;
      })}
    </div>
  );
}
